/*
 *	FadeIn — scroll-triggered, one-shot entrance animations.
 *
 *	Markup: data-inview="reveal" | "fade" | "split"
 *
 *		reveal — opacity fade with a slight upward drift (default).
 *		fade   — opacity fade only, no Y drift.
 *		split  — per-line text reveal: lines wipe up out of a clipping mask,
 *		         staggered, via GSAP SplitText.
 *
 *	Targets start hidden in CSS (`html:not(.no-js) [data-inview] { opacity: 0 }`)
 *	so there's no flash before this runs. Each element animates once per
 *	page-view (ScrollTrigger `once: true`).
 *
 *	A reveal can carry `data-inview-after-split` to defer until the split in its
 *	container has finished animating (instead of its own scroll trigger) — e.g.
 *	a button that should appear after the heading/copy above it has revealed.
 *
 *	Everything waits for web fonts first: SplitText line breaks and trigger
 *	positions must be measured against the real typeface, and headings sized by
 *	FitHeading must have settled, or reveals fire against a stale layout.
 *
 *	`data-inview` elements that live inside a FitHeading node are skipped — that
 *	behaviour owns the split itself, because it re-sizes and re-wraps the
 *	heading and SplitText must not fight it. See behaviours/FitHeading.js.
 */

import whenFontsReady from '@/utils/whenFontsReady';
import { loadGsap, refreshScroll } from '@/utils/gsap';
import splitReveal from '@/utils/splitReveal';

const REDUCED_MOTION = window.matchMedia(
	'(prefers-reduced-motion: reduce)'
).matches;

// Mobile viewports are short enough that waiting for "top 60%" leaves a
// stretch of blank space visible while scrolling — trigger earlier there.
// A function re-evaluates on every ScrollTrigger refresh (incl. its own
// resize handling), so this stays correct if the viewport crosses the
// breakpoint after load instead of locking in whatever was true at mount.
const revealStart = () => (window.innerWidth <= 767 ? 'top 80%' : 'top 60%');

const REVEAL = {
	from: { opacity: 0, y: 24 },
	to: { opacity: 1, y: 0, duration: 1, ease: 'power3.out' },
	start: revealStart,
};

// Same as REVEAL but opacity only — no Y drift (data-inview="fade").
const FADE = {
	from: { opacity: 0 },
	to: { opacity: 1, duration: 1, ease: 'power3.out' },
	start: 'top 60%',
};

const configFor = el => (el.dataset.inview === 'fade' ? FADE : REVEAL);

const SPLIT_START = 'top 60%';

// A deferred reveal (data-inview-after-split) starts this many seconds before
// its split finishes, so the two overlap rather than running back-to-back.
const SPLIT_FOLLOW_LEAD = 0.3;

async function FadeIn({ node }) {
	const targets = [...node.querySelectorAll('[data-inview]')].filter(
		el => !el.closest('[data-behaviour="FitHeading"]')
	);
	if (!targets.length) return () => {};

	// Reduced motion: reveal everything immediately, animate nothing.
	if (REDUCED_MOTION) {
		targets.forEach(el => el.style.setProperty('opacity', '1'));
		return () => {};
	}

	let gsapRef = null;
	const tweens = [];
	const splits = [];

	try {
		await whenFontsReady();

		const { gsap } = await loadGsap();
		gsapRef = gsap;

		const splitTargets = targets.filter(el => el.dataset.inview === 'split');
		const splitSet = new Set(splitTargets);

		// A reveal can opt to wait for the split in its container — find that
		// split by walking up to the nearest ancestor that contains one.
		const findSplitFor = el => {
			let cur = el.parentElement;
			while (cur) {
				const found = [...cur.querySelectorAll('[data-inview="split"]')].find(
					s => splitSet.has(s)
				);
				if (found) return found;
				cur = cur.parentElement;
			}
			return null;
		};

		// splitEl -> [paused reveal tweens waiting on it]
		const deferredBySplit = new Map();

		const reveal = el => {
			const cfg = configFor(el);
			tweens.push(
				gsap.fromTo(el, cfg.from, {
					...cfg.to,
					scrollTrigger: { trigger: el, start: cfg.start, once: true },
				})
			);
		};

		targets
			.filter(el => el.dataset.inview !== 'split')
			.forEach(el => {
				const split =
					el.hasAttribute('data-inview-after-split') && findSplitFor(el);
				if (split) {
					// Created paused so the from-state hides it now; played when the
					// governing split has finished (no scroll trigger of its own).
					const cfg = configFor(el);
					const tween = gsap.fromTo(el, cfg.from, {
						...cfg.to,
						paused: true,
					});
					tweens.push(tween);
					deferredBySplit.set(split, [
						...(deferredBySplit.get(split) ?? []),
						tween,
					]);
				} else {
					reveal(el);
				}
			});

		// Standalone splits re-wrap themselves on resize (autoSplit). When a split
		// finishes, play any reveals deferred behind it.
		const created = await Promise.all(
			splitTargets.map(el =>
				splitReveal(el, {
					start: SPLIT_START,
					autoSplit: true,
					onRevealLead: SPLIT_FOLLOW_LEAD,
					onReveal: () => {
						(deferredBySplit.get(el) ?? []).forEach(tween => tween.play());
					},
				})
			)
		);
		splits.push(...created);

		// Recompute every trigger now this run's triggers exist — pins and
		// heading fits elsewhere may have shifted the page underneath them.
		refreshScroll();
	} catch (error) {
		console.error('FadeIn failed to set up reveals:', error);
		targets.forEach(el => el.style.setProperty('opacity', '1'));
		return () => {};
	}

	return () => {
		tweens.forEach(tween => {
			tween.scrollTrigger?.kill();
			tween.kill();
		});
		splits.forEach(controller => controller.revert());
		if (gsapRef) gsapRef.killTweensOf(targets);
	};
}

export default FadeIn;
